import React from 'react';
import { Container, Row, Col, Card, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import PublicSiteNavbar from '../../components/PublicSiteNavbar';
import PublicSiteFooter from '../../components/PublicSiteFooter';
import NonIndexableHelmet from '../../components/NonIndexableHelmet';
import { getLegalMeta } from '../../data/nonIndexableMeta';
import { useLocale } from '../../contexts/LocaleContext';
import { getDateLocale } from '../../utils/localePaths';
import { COMPANY } from '../../data/legalCompanyInfo';

const CguPage = () => {
  const { locale } = useLocale();
  const dateLoc = getDateLocale(locale);
  return (
    <div className="min-vh-100 bg-light legal-page">
      <NonIndexableHelmet {...getLegalMeta('/legal/cgu')} />
      <PublicSiteNavbar />

      {/* Content */}
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col lg={10} xl={8}>
            <Card className="shadow-sm border-0">
              <Card.Body className="p-3 p-md-4 p-lg-5">
                <h1 className="fw-bold mb-4">Conditions Générales d'Utilisation (CGU)</h1>
                <p className="text-muted mb-4">
                  Dernière mise à jour : {new Date().toLocaleDateString(dateLoc)}
                </p>

                <Alert variant="info" className="border-0">
                  <i className="bi bi-info-circle me-2"></i>
                  <strong>Acceptation des CGU</strong>
                  <p className="mb-0 mt-2 small">
                    L'utilisation du service Assistance Video implique l'acceptation pleine et entière
                    des présentes Conditions Générales d'Utilisation. Si vous n'acceptez pas ces conditions,
                    veuillez ne pas utiliser le service.
                  </p>
                </Alert>

                <hr className="my-4" />

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">1. Objet</h2>
                  <p>
                    Les présentes Conditions Générales d'Utilisation (ci-après « CGU ») ont pour objet de
                    définir les modalités et conditions d'utilisation du service <strong>Assistance Video</strong>,
                    plateforme de visioconférence dédiée à l'assistance technique à distance, éditée par la
                    société LEAGORA SAS.
                  </p>
                  <p>
                    Le service permet aux professionnels (ci-après « Opérateurs ») de créer des liens de
                    connexion vidéo sécurisés pour assister leurs clients (ci-après « Clients ») à distance
                    en temps réel.
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">2. Éditeur du service</h2>
                  <p>Le service {COMPANY.siteName} est édité par :</p>
                  <ul className="list-unstyled ms-3">
                    <li><strong>Raison sociale :</strong> {COMPANY.legalName}</li>
                    <li><strong>Capital social :</strong> {COMPANY.capital}</li>
                    <li><strong>Siège social :</strong> {COMPANY.address}</li>
                    <li><strong>SIREN :</strong> {COMPANY.siren}</li>
                    <li><strong>SIRET :</strong> {COMPANY.siret}</li>
                    <li><strong>RCS :</strong> {COMPANY.rcs}</li>
                    <li><strong>N° TVA intracommunautaire :</strong> {COMPANY.vat}</li>
                    <li><strong>Email :</strong> <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a></li>
                  </ul>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">3. Définitions</h2>
                  <ul>
                    <li><strong>Service :</strong> La plateforme Assistance Video accessible via le site web</li>
                    <li><strong>Opérateur :</strong> Utilisateur professionnel disposant d'un compte sur le service</li>
                    <li><strong>Client :</strong> Personne rejoignant une session vidéo via un lien partagé par un Opérateur</li>
                    <li><strong>Session :</strong> Connexion vidéo entre un Opérateur et un Client</li>
                    <li><strong>Crédit :</strong> Unité permettant de créer une session (1 crédit = 1 session)</li>
                    <li><strong>Lien de dépannage :</strong> URL unique permettant à un Client de rejoindre une session</li>
                  </ul>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">4. Inscription et création de compte</h2>
                  
                  <h3 className="h6 fw-semibold mt-4 mb-2">3.1. Conditions d'inscription</h3>
                  <p>
                    L'utilisation du service en tant qu'Opérateur nécessite la création d'un compte.
                    L'inscription est réservée aux personnes majeures et aux professionnels.
                  </p>

                  <h3 className="h6 fw-semibold mt-4 mb-2">3.2. Processus d'inscription</h3>
                  <p>
                    L'inscription se fait via un système de « magic link » (lien magique) :
                  </p>
                  <ol>
                    <li>L'utilisateur saisit son adresse email</li>
                    <li>Un lien de connexion unique est envoyé par email</li>
                    <li>Le compte est créé automatiquement lors du premier clic sur le lien</li>
                    <li>Les connexions suivantes utilisent le même système de magic link</li>
                  </ol>

                  <h3 className="h6 fw-semibold mt-4 mb-2">3.3. Exactitude des informations</h3>
                  <p>
                    L'utilisateur s'engage à fournir des informations exactes et à jour. Il est responsable
                    de la confidentialité de son adresse email et de l'accès à sa boîte de réception.
                  </p>

                  <h3 className="h6 fw-semibold mt-4 mb-2">3.4. Compte unique</h3>
                  <p>
                    Chaque utilisateur ne peut créer qu'un seul compte. La création de comptes multiples
                    est interdite et peut entraîner la suspension de tous les comptes concernés.
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">5. Utilisation du service</h2>
                  
                  <h3 className="h6 fw-semibold mt-4 mb-2">4.1. Fonctionnement du service</h3>
                  <p>Le service permet aux Opérateurs de :</p>
                  <ul>
                    <li>Créer des liens de dépannage uniques</li>
                    <li>Partager ces liens avec leurs Clients (email, SMS, WhatsApp, etc.)</li>
                    <li>Rejoindre des sessions vidéo avec leurs Clients</li>
                    <li>Gérer leur historique de sessions</li>
                    <li>Consulter leurs statistiques d'utilisation</li>
                  </ul>

                  <h3 className="h6 fw-semibold mt-4 mb-2">4.2. Système de crédits</h3>
                  <p>
                    L'utilisation du service nécessite des crédits. Un crédit est consommé au démarrage de chaque session vidéo. Les crédits sont achetés sous forme de packs (voir CGV).
                  </p>
                  <ul>
                    <li>1 crédit = 1 session de dépannage vidéo</li>
                    <li>Le crédit est consommé automatiquement au démarrage de la session</li>
                    <li>Les sessions sont illimitées en durée</li>
                    <li>Les crédits n'expirent jamais</li>
                    <li>Les crédits ne sont pas remboursables</li>
                  </ul>

                  <h3 className="h6 fw-semibold mt-4 mb-2">4.3. Durée des sessions</h3>
                  <p>
                    Les sessions vidéo sont illimitées en durée. Un crédit est consommé au démarrage de la session, quelle que soit sa durée effective.
                  </p>

                  <h3 className="h6 fw-semibold mt-4 mb-2">4.4. Accès des Clients</h3>
                  <p>
                    Les Clients n'ont pas besoin de créer de compte. Ils accèdent aux sessions vidéo
                    directement via les liens de dépannage partagés par les Opérateurs. Aucune installation
                    d'application n'est requise.
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">6. Obligations de l'utilisateur</h2>
                  
                  <h3 className="h6 fw-semibold mt-4 mb-2">5.1. Usage conforme</h3>
                  <p>L'utilisateur s'engage à :</p>
                  <ul>
                    <li>Utiliser le service de manière conforme à sa destination (assistance technique professionnelle)</li>
                    <li>Respecter les lois et règlements en vigueur</li>
                    <li>Ne pas utiliser le service à des fins illégales, frauduleuses ou contraires aux bonnes mœurs</li>
                    <li>Ne pas porter atteinte aux droits de tiers</li>
                    <li>Ne pas diffuser de contenus illicites, diffamatoires, violents ou pornographiques</li>
                  </ul>

                  <h3 className="h6 fw-semibold mt-4 mb-2">5.2. Sécurité</h3>
                  <p>L'utilisateur s'engage à :</p>
                  <ul>
                    <li>Protéger l'accès à sa boîte email</li>
                    <li>Ne pas partager ses liens de connexion (magic links)</li>
                    <li>Signaler immédiatement toute utilisation non autorisée de son compte</li>
                    <li>Ne pas tenter de contourner les mesures de sécurité du service</li>
                  </ul>

                  <h3 className="h6 fw-semibold mt-4 mb-2">5.3. Respect de la confidentialité</h3>
                  <p>
                    L'utilisateur s'engage à respecter la confidentialité des informations échangées lors
                    des sessions vidéo et à ne pas enregistrer ou diffuser les sessions sans le consentement
                    explicite de toutes les parties.
                  </p>

                  <h3 className="h6 fw-semibold mt-4 mb-2">5.4. Usage professionnel</h3>
                  <p>
                    Le service est destiné à un usage professionnel. L'utilisateur s'engage à utiliser le
                    service dans le cadre de son activité professionnelle d'assistance, de support ou de
                    dépannage.
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">7. Propriété intellectuelle</h2>
                  <p>
                    L'ensemble des éléments du service (structure, design, textes, graphismes, logos,
                    icônes, sons, logiciels, etc.) est la propriété exclusive de LEAGORA ou de ses
                    partenaires.
                  </p>
                  <p>
                    Toute reproduction, représentation, modification, publication, adaptation de tout ou
                    partie des éléments du service, quel que soit le moyen ou le procédé utilisé, est
                    interdite, sauf autorisation écrite préalable de LEAGORA.
                  </p>
                  <p>
                    La marque « Assistance Video » ainsi que les logos sont des marques déposées ou en
                    cours de dépôt. Toute utilisation non autorisée de ces marques est interdite.
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">8. Disponibilité du service</h2>
                  
                  <h3 className="h6 fw-semibold mt-4 mb-2">7.1. Disponibilité</h3>
                  <p>
                    LEAGORA s'efforce d'assurer une disponibilité du service 24h/24 et 7j/7. Toutefois,
                    LEAGORA ne peut garantir une disponibilité absolue et se réserve le droit d'interrompre
                    temporairement le service pour des raisons de maintenance, de mise à jour ou de force majeure.
                  </p>

                  <h3 className="h6 fw-semibold mt-4 mb-2">7.2. Maintenance</h3>
                  <p>
                    LEAGORA se réserve le droit d'effectuer des opérations de maintenance programmée ou
                    d'urgence. Dans la mesure du possible, les utilisateurs seront informés à l'avance
                    des interruptions programmées.
                  </p>

                  <h3 className="h6 fw-semibold mt-4 mb-2">7.3. Évolution du service</h3>
                  <p>
                    LEAGORA se réserve le droit de faire évoluer, modifier ou suspendre, sans préavis,
                    le service pour des raisons de maintenance ou pour tout autre motif jugé nécessaire.
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">9. Responsabilités</h2>
                  
                  <h3 className="h6 fw-semibold mt-4 mb-2">8.1. Responsabilité de LEAGORA</h3>
                  <p>
                    LEAGORA met en œuvre tous les moyens raisonnables pour assurer le bon fonctionnement
                    du service. Toutefois, LEAGORA ne peut être tenue responsable :
                  </p>
                  <ul>
                    <li>Des interruptions ou dysfonctionnements du service</li>
                    <li>De la qualité de la connexion internet de l'utilisateur</li>
                    <li>Des problèmes liés au matériel de l'utilisateur (caméra, micro, etc.)</li>
                    <li>Du contenu des échanges entre Opérateurs et Clients</li>
                    <li>De l'utilisation abusive ou frauduleuse du service par un utilisateur</li>
                    <li>Des dommages indirects (perte de chiffre d'affaires, de données, etc.)</li>
                  </ul>

                  <h3 className="h6 fw-semibold mt-4 mb-2">8.2. Limitation de responsabilité</h3>
                  <p>
                    En tout état de cause, la responsabilité de LEAGORA est limitée au montant des sommes
                    effectivement payées par l'utilisateur au cours des 12 derniers mois précédant le fait
                    générateur de responsabilité.
                  </p>

                  <h3 className="h6 fw-semibold mt-4 mb-2">8.3. Responsabilité de l'utilisateur</h3>
                  <p>
                    L'utilisateur est seul responsable :
                  </p>
                  <ul>
                    <li>De l'utilisation qu'il fait du service</li>
                    <li>Du contenu des sessions vidéo qu'il réalise</li>
                    <li>Du respect des droits des tiers</li>
                    <li>De la conformité de son utilisation avec les lois en vigueur</li>
                  </ul>
                  <p>
                    L'utilisateur s'engage à indemniser LEAGORA de tout préjudice résultant d'une
                    utilisation non conforme du service.
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">10. Données personnelles</h2>
                  <p>
                    Les données personnelles collectées dans le cadre de l'utilisation du service sont
                    traitées conformément à notre{' '}
                    <Link to="/legal/privacy/">Politique de Confidentialité</Link> et au Règlement Général
                    sur la Protection des Données (RGPD).
                  </p>
                  <p>
                    Pour plus d'informations sur vos droits et la gestion de vos données, consultez notre
                    page <Link to="/legal/rgpd/">RGPD</Link>.
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">11. Suspension et résiliation</h2>
                  
                  <h3 className="h6 fw-semibold mt-4 mb-2">10.1. Suspension par LEAGORA</h3>
                  <p>
                    LEAGORA se réserve le droit de suspendre ou de résilier, sans préavis ni indemnité,
                    l'accès au service d'un utilisateur en cas de :
                  </p>
                  <ul>
                    <li>Violation des présentes CGU</li>
                    <li>Utilisation frauduleuse ou abusive du service</li>
                    <li>Non-paiement des sommes dues</li>
                    <li>Atteinte aux droits de tiers</li>
                    <li>Comportement nuisible au service ou à sa réputation</li>
                  </ul>

                  <h3 className="h6 fw-semibold mt-4 mb-2">10.2. Résiliation par l'utilisateur</h3>
                  <p>
                    L'utilisateur peut résilier son compte à tout moment en contactant le support à
                    l'adresse <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>. La résiliation
                    entraîne la suppression du compte et la perte des crédits non utilisés (non remboursables).
                  </p>

                  <h3 className="h6 fw-semibold mt-4 mb-2">10.3. Effets de la résiliation</h3>
                  <p>
                    En cas de résiliation, l'utilisateur perd immédiatement l'accès au service et à
                    l'ensemble de ses données. Les crédits non utilisés ne sont pas remboursables.
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">12. Modification des CGU</h2>
                  <p>
                    LEAGORA se réserve le droit de modifier les présentes CGU à tout moment. Les utilisateurs
                    seront informés de toute modification substantielle par email ou via une notification sur
                    le service.
                  </p>
                  <p>
                    L'utilisation du service après modification des CGU vaut acceptation des nouvelles conditions.
                    Si l'utilisateur n'accepte pas les nouvelles conditions, il doit cesser d'utiliser le service.
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">13. Loi applicable et juridiction</h2>
                  <p>
                    Les présentes CGU sont régies par le droit français. En cas de litige relatif à
                    l'interprétation ou à l'exécution des présentes, et à défaut d'accord amiable, le
                    litige sera porté devant les tribunaux compétents de Paris, France.
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">14. Contact</h2>
                  <p>
                    Pour toute question concernant les présentes CGU, vous pouvez contacter {COMPANY.legalName} :
                  </p>
                  <ul className="list-unstyled ms-3">
                    <li><strong>Email :</strong> <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a></li>
                    <li><strong>Adresse :</strong> {COMPANY.address}</li>
                    <li><strong>SIREN :</strong> {COMPANY.siren}</li>
                    <li><strong>RCS :</strong> {COMPANY.rcs}</li>
                  </ul>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">15. Divisibilité</h2>
                  <p>
                    Si une ou plusieurs stipulations des présentes CGU sont tenues pour non valides ou
                    déclarées comme telles en application d'une loi, d'un règlement ou à la suite d'une
                    décision définitive d'une juridiction compétente, les autres stipulations garderont
                    toute leur force et leur portée.
                  </p>
                </section>
              </Card.Body>
            </Card>

            {/* Navigation vers autres pages légales */}
            <div className="mt-4 text-center">
              <p className="text-muted small mb-2">Voir aussi :</p>
              <div className="d-flex flex-wrap justify-content-center gap-3">
                <Link to="/legal/mentions/" className="text-decoration-none">Mentions légales</Link>
                <span className="text-muted">•</span>
                <Link to="/legal/cgv/" className="text-decoration-none">CGV</Link>
                <span className="text-muted">•</span>
                <Link to="/legal/privacy/" className="text-decoration-none">Confidentialité</Link>
                <span className="text-muted">•</span>
                <Link to="/legal/rgpd/" className="text-decoration-none">RGPD</Link>
              </div>
            </div>
          </Col>
        </Row>
      </Container>

      <PublicSiteFooter />
    </div>
  );
};

export default CguPage;

